import cn from 'classnames';
import { useSelector } from 'react-redux';

import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';
import { Col, Section, Row } from '@guestyci/foundation/Layout';
import OutlineButton from '@guestyci/foundation/OutlineButton';
import RaisedButton from '@guestyci/foundation/RaisedButton';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import useMediaQuery from '@guestyci/foundation/useMediaQuery';
import { Breakpoints } from '@guestyci/foundation/Layout/types';

import breakpoints from 'app/shareable/theme/breakpoints';
import { selectTrialPeriod } from 'app/store/selector';
import { useAuthorizationContext } from 'app/module/Authorization/AuthorizationContainer';
import track from 'utils/dio/trial-locked-flow';
import config from 'config';
import { SubscriptionCardView } from './Offers';

const useClasses = createStyles((theme: typeof defaultTheme) => {
  const tablet = breakpoints.max.lg;
  const largeDesktop = breakpoints.max.xlg;
  const smallDesktop = breakpoints.max.xl;

  return {
    root: {
      borderRadius: '0.625rem',
      maxWidth: '21.25rem',

      [tablet]: {
        maxWidth: 'unset',
        width: '100%',
      },
    },
    priceContainer: {
      fontSize: '5rem',
      lineHeight: '1',
      position: 'relative',
      '&:before': {
        content: 'attr(data-ui--discount-price)',
        fontSize: '0.35em',
        position: 'absolute',
        top: '-1.5em', // value based on the current font-size
        right: 0,
        textDecoration: 'line-through',
        fontWeight: 400,
      },
      '&[data-ui--font-size-constraint="4"], &[data-ui--font-size-constraint="5"]':
        {
          fontSize: '3rem',
        },
      [largeDesktop]: {
        fontSize: 'min(4.5rem, 4vw)',
        '&[data-ui--font-size-constraint="4"], &[data-ui--font-size-constraint="5"]':
          {
            fontSize: '2rem',
          },
      },
      [smallDesktop]: {
        fontSize: 'min(3.5rem, 3vw)',
        '&[data-ui--font-size-constraint="3"]': {
          fontSize: '3.5rem',
          '&:before': {
            fontSize: '0.875rem',
          },
        },
        '&[data-ui--font-size-constraint="4"]': {
          fontSize: '2.5rem',
        },
      },
      [tablet]: {
        '&[data-ui--font-size-constraint="3"]': {
          fontSize: '2.5rem',
        },
      },
    },
    discountBanner: {
      color: theme.palette.text.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '12.5rem',
      height: '12.5rem',
      position: 'absolute',
      left: '0',
      top: '0',
      transform: 'translate(-30%, -30%)',
      fontSize: '0.875rem',
      fontWeight: 700,
      zIndex: 0,

      '&:before': {
        content: 'attr(data-ui--discount-stripe)',
        backgroundColor: theme.palette.primary.default,
        height: '1.75rem',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transform: 'rotate(-45deg)',
        userSelect: 'none',
      },
    },
    borderColor: {
      borderColor: theme.palette.border,
    },
    cardPreselected: {
      boxShadow:
        '0px 29px 12px rgba(0, 37, 92, 0.01), 0px 16px 10px rgba(0, 37, 92, 0.05), 0px 7px 7px rgba(0, 37, 92, 0.09), 0px 2px 4px rgba(0, 37, 92, 0.1), 0px 0px 0px rgba(0, 37, 92, 0.1);',
      borderColor: theme.palette.primary.default,
      [tablet]: {
        order: -1,
      },
    },
  };
});

interface SubscriptionCardProps {
  isPreselected: boolean;
  data: SubscriptionCardView;
}

function SubscriptionCard({ isPreselected, data }: SubscriptionCardProps) {
  const { state } = useAuthorizationContext();
  const isSmallDesktop = useMediaQuery({ maxWidth: 'xl' });
  const isTablet = useMediaQuery({ maxWidth: 'lg' });
  const classes = useClasses();
  const { subscriptionCancelled } = useSelector(selectTrialPeriod);

  const { sale, discount, price, title, listingCount, footerText, id } = data;

  return (
    <Col
      className={cn(classes.root, classes.borderColor, 'b-1 overflow-hidden', {
        [classes.cardPreselected]: isPreselected,
      })}
      spacing={isTablet ? 3 : 4}
      justify="between"
    >
      <Row>
        <Col
          className="relative text-center zindex-1"
          justify="center"
          span={12}
        >
          <Section flex={false} gutter={isTablet ? '3 0 0' : '8 10 0'}>
            {sale && (
              <div
                className={classes.discountBanner}
                data-ui--discount-stripe={sale}
              />
            )}
            <TextField
              className="mb-2 first-letter-upper-case"
              bold={isTablet}
              color="dark"
              variant={isTablet ? 'h4' : 'h2'}
            >
              {title}
            </TextField>
          </Section>
          <Col spacing={isTablet ? 0 : 1} className="px-2">
            <TextField variant={isTablet ? 'h7' : 'h5'}>
              Plan includes
              <span className="px-1">{listingCount}</span>
              listing
              {listingCount === 1 ? '' : 's'}.
            </TextField>
            <TextField variant={isTablet ? 'h7' : 'h5'}>
              Easily update plan as you add more listings
            </TextField>
          </Col>
        </Col>
      </Row>

      <div
        className={cn(classes.borderColor, 'bb-1', isTablet ? 'mx-3' : 'mx-4')}
      />

      <Row
        spacing={1}
        align="center"
        justify="center"
        className={cn({
          'my-4': isSmallDesktop,
          'my-9': !isSmallDesktop,
        })}
        wrap={isSmallDesktop}
      >
        <Col
          breakpoints={
            {
              xlg: 7,
              xl: 12,
            } as Breakpoints
          }
          align="end"
        >
          <TextField
            variant="h1"
            className={classes.priceContainer}
            data-ui--discount-price={discount}
            data-ui--font-size-constraint={price.length}
          >
            {price}
          </TextField>
        </Col>
        <Col
          breakpoints={
            {
              xlg: 5,
              xl: 12,
            } as Breakpoints
          }
          align={isSmallDesktop ? 'center' : 'start'}
        >
          <Col>
            <Row spacing={1}>
              <TextField variant={isTablet ? 'h5' : 'h4'}>for</TextField>
              <TextField variant={isTablet ? 'h5' : 'h4'}>
                {listingCount}
              </TextField>
              <TextField variant={isTablet ? 'h5' : 'h4'}>
                listing
                {listingCount === 1 ? '' : 's'}
              </TextField>
            </Row>

            <TextField display="block" className="text-nowrap">
              per month*
            </TextField>
          </Col>
        </Col>
      </Row>

      <div className={cn(classes.borderColor, 'bb-1 mx-4')} />

      <Section gutter={isTablet ? '0 2 4 2' : '2 2 7 2'} flex={false}>
        <Col
          align="center"
          breakpoints={
            {
              xlg: 6,
              xl: 8,
            } as Breakpoints
          }
          spacing={isTablet ? 3 : 4}
          className="mx-auto"
        >
          <TextField variant="h5">{footerText}</TextField>

          <a
            href={`${config.creditCardPageAuth}?userId=${state.userId}&subscription=${id}`}
            rel="noreferrer"
            target="_blank"
            className="width-fill no-underline"
            onClick={() =>
              track.endOfTrialPopUpClickSubscribe(id, !subscriptionCancelled)
            }
          >
            {isPreselected ? (
              <RaisedButton className="text-capitalize width-fill py-2">
                Subscribe
              </RaisedButton>
            ) : (
              <OutlineButton className="text-capitalize width-fill py-2">
                Subscribe
              </OutlineButton>
            )}
          </a>
        </Col>
      </Section>
    </Col>
  );
}

export default SubscriptionCard;
